import { Injectable } from '@angular/core';
import { combineQueries, EntityState, Order, QueryConfig, QueryEntity } from '@datorama/akita';
import { GuideRunStatus, NeedsType } from '@libs/shared/interfaces/couch/models/core';
import { GuidePouchContent } from '@libs/shared/interfaces/couch/models/guide';
import { AssistantStatus, GuideRunPouchContent, PhaseStatus } from '@libs/shared/interfaces/couch/models/guide-run';
import { GuideQuery } from '@libs/shared/interfaces/couch/queries/guide';
import { GuideRunStore } from '@libs/shared/interfaces/couch/stores/guide-run';
import {
  getActiveGuideRunPhase,
  getActiveGuideRunPhaseSameDay,
  getNextPhaseInSequentialList,
  getNextScheduledPhases,
  getPreviousPhases,
} from '@libs/shared/utils';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { map, Observable } from 'rxjs';

export type GuideRunPouchContentWithGuide = GuideRunPouchContent & { guide: GuidePouchContent };

@QueryConfig({
  sortBy: 'created',
  sortByOrder: Order.DESC,
})
@Injectable({
  providedIn: 'root',
})
export class GuideRunQuery extends QueryEntity<EntityState<GuideRunPouchContent, string>> {
  public all$ = this.selectAll();

  constructor(
    private readonly guideQuery: GuideQuery,
    protected override store: GuideRunStore,
  ) {
    super(store);
  }

  public getById(id: string): GuideRunPouchContent | undefined {
    return this.getAll().find((guideRun) => guideRun._id === id);
  }

  public allWithAttendanceId(attendanceId: string): GuideRunPouchContent[] | undefined {
    return this.getAll().filter((guideRun) => guideRun.generali?.attendanceId === attendanceId);
  }

  public allWithGuide(): Observable<GuideRunPouchContentWithGuide[]> {
    return combineQueries([this.selectAll(), this.guideQuery.all$]).pipe(
      map(([guideRuns, guides]) => {
        return guideRuns.map((guideRun) => {
          return {
            ...guideRun,
            guide: guides.find((guide) => guide._id === `guide_${guideRun.guideId}`),
          } as GuideRunPouchContentWithGuide;
        });
      }),
    );
  }

  public allChronological(): Observable<GuideRunPouchContent[]> {
    return this.selectAll({ sortBy: 'created', sortByOrder: Order.ASC });
  }

  public byGuideRunId(guideRunId: string): Observable<GuideRunPouchContent | undefined> {
    return this.selectEntity(guideRunId);
  }

  public phase(guideRunId: string, phaseId: string): Observable<PhaseStatus | undefined> {
    return this.byGuideRunId(guideRunId).pipe(
      map((guideRun) => guideRun?.phases.find((phase) => phase.id === phaseId)),
    );
  }

  public assistantsByGuideRunId(guideRunId: string): Observable<AssistantStatus[]> {
    return this.byGuideRunId(guideRunId).pipe(map((guideRun) => guideRun?.assistants || []));
  }

  public assistantByGuideRunId(guideRunId: string, assistantId: string): Observable<AssistantStatus | undefined> {
    return this.byGuideRunId(guideRunId).pipe(
      map((guideRun) => guideRun?.assistants.find((assistant) => assistant.id === assistantId)),
    );
  }

  public activeAssistantsByGuideRunId(guideRunId: string): Observable<AssistantStatus[]> {
    return this.assistantsByGuideRunId(guideRunId).pipe(
      map((assistants) => assistants.filter((assistant) => assistant.started && !assistant.ended)),
    );
  }

  public getByGuideRunId(guideRunId: string): GuideRunPouchContent | undefined {
    return this.getEntity(guideRunId);
  }

  public getGuideIdByGuideRunId(guideRunId: string): string | undefined {
    return this.getByGuideRunId(guideRunId)?.guideId;
  }

  public getGuideVoucherByGuideRunId(guideRunId: string): string | undefined {
    return this.getByGuideRunId(guideRunId)?.guideVoucher;
  }

  public mostRecent(): Observable<GuideRunPouchContent> {
    return this.selectAll({ sortBy: 'created', sortByOrder: Order.DESC, limitTo: 1 }).pipe(
      map((guideRuns) => {
        return guideRuns[0];
      }),
    );
  }

  public getEnabled(): GuideRunPouchContent | undefined {
    return this.getAll().find((guideRun) => guideRun.status === GuideRunStatus.enabled);
  }

  public getByDate(date: Date): GuideRunPouchContent | undefined {
    return this.getAll().find((guideRun) => {
      return guideRun.phases.some((phase) => {
        return (
          phase.started &&
          isAfter(date, parseISO(phase.started)) &&
          (phase.ended === null || (phase.ended && isBefore(date, parseISO(phase.ended))))
        );
      });
    });
  }

  public enabled(): Observable<GuideRunPouchContent | undefined> {
    return this.selectAll().pipe(
      map((guideRuns) => guideRuns.find((guideRun) => guideRun.status === GuideRunStatus.enabled)),
    );
  }

  public getActiveNeedsSensorPhases(guideId: string, guideRunId: string): Observable<PhaseStatus[] | undefined> {
    return this.byGuideRunId(guideRunId).pipe(
      map((guideRun) =>
        guideRun?.phases.filter((phase) => phase.started && phase.needs.find((p) => p.type === NeedsType.tg_sensor)),
      ),
    );
  }

  public getEnabledWithActivePhase(hasAlternatePhaseEnds?: boolean): GuideRunPouchContent | undefined {
    const activeGuideRun = this.getEnabled();

    if (!activeGuideRun) {
      return;
    }

    return getActiveGuideRunPhase(activeGuideRun, new Date(), hasAlternatePhaseEnds) ? activeGuideRun : undefined;
  }

  public getActivePhase(hasAlternatePhaseEnds?: boolean): PhaseStatus | undefined {
    const activeGuideRun = this.getEnabled();

    if (!activeGuideRun) {
      return;
    }

    return getActiveGuideRunPhase(activeGuideRun, new Date(), hasAlternatePhaseEnds);
  }

  public activePhase(hasAlternatePhaseEnds?: boolean): Observable<PhaseStatus | undefined> {
    return this.enabled().pipe(
      map((guideRun) => {
        if (!guideRun) {
          return;
        }

        return getActiveGuideRunPhase(guideRun, new Date(), hasAlternatePhaseEnds);
      }),
    );
  }

  public getActivePhaseAt(date: string, hasAlternatePhaseEnds?: boolean): PhaseStatus | undefined {
    const activeGuideRun = this.getEnabled();

    if (!activeGuideRun) {
      return;
    }

    return getActiveGuideRunPhase(activeGuideRun, parseISO(date), hasAlternatePhaseEnds);
  }

  public getNextPhase(hasAlternatePhaseEnds?: boolean): PhaseStatus | undefined {
    const activeGuideRun = this.getEnabled();

    if (!activeGuideRun) {
      return;
    }

    return getNextPhaseInSequentialList(activeGuideRun, new Date(), hasAlternatePhaseEnds);
  }

  public getNextScheduledPhase(): PhaseStatus | undefined {
    const activeGuideRun = this.getEnabled();

    if (!activeGuideRun) {
      return;
    }

    return getNextScheduledPhases(activeGuideRun, new Date()).shift();
  }

  public getByPhaseId(guideRunId: string, phaseId: string): PhaseStatus | undefined {
    const guideRun = this.getAll().find((guideRun) => guideRun._id === guideRunId);

    if (!guideRun) {
      return;
    }

    return guideRun.phases.find((phase) => phase.id === phaseId);
  }

  public getPreviousPhases(): PhaseStatus[] {
    const activeGuideRun = this.getEnabled();

    if (!activeGuideRun) {
      return [];
    }

    return getPreviousPhases(activeGuideRun, new Date());
  }

  public isPhaseActive(_: string, phaseId: string, hasAlternatePhaseEnds?: boolean): boolean {
    return this.getActivePhase(hasAlternatePhaseEnds)?.id === phaseId;
  }

  public getPhaseByDate(date: Date, guideRun: GuideRunPouchContent): PhaseStatus | undefined {
    return getActiveGuideRunPhaseSameDay(guideRun, date);
  }

  public getFirstPhase(guideRun: GuideRunPouchContent): PhaseStatus | undefined {
    return guideRun.phases[0];
  }

  public getLastPhase(guideRun: GuideRunPouchContent): PhaseStatus | undefined {
    return guideRun.phases[guideRun.phases.length - 1];
  }
}
